import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Cog,
  FileText,
  Layers,
  Loader2,
  MessageSquare,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Wand2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";

/**
 * Admin Studio — Ask Informed (Phase 1f).
 *
 * Route: `/admin/academy/ask`. Placed as a peer tab under the existing
 * Academy Studio surface (Assessments, Certificates, Ask Informed).
 * RBAC: `view_academy` + `academy_admin_studio_enabled`.
 *
 * Surfaces:
 *   1. LLM configuration (provider + model + configured status)
 *   2. Grounding index summary — 5 canonical states + total chunks
 *   3. Sources table with per-source reindex
 *   4. Live probe with warning UI + sanitized result
 *
 * Never displays raw provider payloads, credentials, or system_config
 * secrets. Every learner-safe field is a plain React text render.
 */
const STATE_META = {
  indexed_current: { label: "Indexed", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5", Icon: CheckCircle2 },
  indexing_pending: { label: "Pending", cls: "text-amber-300 border-amber-400/40 bg-amber-400/5", Icon: Clock },
  stale: { label: "Stale", cls: "text-sky-300 border-sky-400/40 bg-sky-400/5", Icon: RefreshCw },
  indexing_failed: { label: "Failed", cls: "text-red-400 border-red-400/40 bg-red-400/5", Icon: XCircle },
  unavailable_or_intentionally_unindexed: { label: "Unavailable", cls: "text-v2-text-muted border-v2-border bg-v2-surface-muted", Icon: FileText },
};

export default function AdminAskInformed() {
  return (
    <div data-testid="admin-academy-ask" className="max-w-6xl mx-auto px-4 md:px-8 py-10">
      <header className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">Academy Studio</p>
        <h1
          className="mt-1 text-[32px] font-semibold text-v2-text flex items-center gap-2"
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
        >
          <Sparkles className="w-6 h-6 text-[#A78BFA]" />
          Ask Informed
        </h1>
        <p className="mt-2 text-sm text-v2-text-muted max-w-2xl">
          Configure the coach LLM, monitor grounding index states, and run a live provider probe.
          Learner-facing Ask availability is separately controlled by the <code className="text-xs bg-v2-surface px-1.5 py-0.5 rounded">academy_ask_informed_enabled</code> feature flag.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ConfigPanel />
        <ProbePanel />
      </div>

      <SummaryPanel />
      <SourcesTable />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config panel
// ---------------------------------------------------------------------------

function ConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const r = await api.get("/admin/academy/ask/config");
      setCfg(r.data);
      setDirty({});
      setStatus("ready");
    } catch (err) {
      setError(formatApiError(err));
      setStatus("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/academy/ask/config", dirty);
      setCfg(r.data);
      setDirty({});
      toast.success("Ask configuration saved.");
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="admin-ask-config" className="rounded-xl border border-v2-border bg-v2-surface p-6">
      <div className="flex items-center gap-2 mb-4">
        <Cog className="w-4 h-4 text-v2-text-muted" />
        <h2 className="text-sm font-semibold text-v2-text uppercase tracking-[0.12em]">LLM configuration</h2>
      </div>
      {status === "loading" && (
        <div className="text-xs text-v2-text-muted flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}
      {status === "error" && (
        <div data-testid="admin-ask-config-error" className="text-xs text-red-400">{error}</div>
      )}
      {status === "ready" && cfg && (
        <div className="space-y-4">
          <Field
            label="Provider"
            hint="Allowed: emergent, openai, fake (test-only)."
            testId="admin-ask-provider-input"
            value={dirty.academy_ask_provider ?? cfg.provider}
            onChange={(v) => setDirty({ ...dirty, academy_ask_provider: v })}
          />
          <Field
            label="Model"
            hint="Default gpt-4o-mini. No silent fallback."
            testId="admin-ask-model-input"
            value={dirty.academy_ask_model ?? cfg.model}
            onChange={(v) => setDirty({ ...dirty, academy_ask_model: v })}
          />
          <div className="text-xs flex items-center gap-2">
            <span className="text-v2-text-muted">Configured:</span>
            <span
              data-testid="admin-ask-configured-status"
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                cfg.provider_configured ? "text-emerald-400 border-emerald-400/40 bg-emerald-400/5" : "text-red-400 border-red-400/40 bg-red-400/5"
              }`}
            >
              {cfg.provider_configured ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              {cfg.provider_configured ? "Yes" : "Missing credential"}
            </span>
            <span className="text-v2-text-muted ml-2">Class:</span>
            <code data-testid="admin-ask-provider-class" className="text-[11px] bg-v2-bg px-1.5 py-0.5 rounded border border-v2-border">
              {cfg.provider_class}
            </code>
          </div>
          <button
            data-testid="admin-ask-config-save"
            onClick={save}
            disabled={saving || Object.keys(dirty).length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#A78BFA] text-black text-sm font-medium hover:bg-[#9D7AEA] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cog className="w-4 h-4" />}
            {saving ? "Saving…" : "Save configuration"}
          </button>
        </div>
      )}
    </section>
  );
}

function Field({ label, hint, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-xs text-v2-text-muted block mb-1">{label}</span>
      <input
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-v2-bg border border-v2-border rounded-md px-3 py-2 text-sm text-v2-text"
      />
      {hint ? <span className="text-[10px] text-v2-text-muted mt-1 block">{hint}</span> : null}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Probe panel
// ---------------------------------------------------------------------------

function ProbePanel() {
  const [probing, setProbing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setProbing(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.post("/admin/academy/ask/probe");
      setResult(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setProbing(false);
    }
  };

  return (
    <section data-testid="admin-ask-probe" className="rounded-xl border border-v2-border bg-v2-surface p-6">
      <div className="flex items-center gap-2 mb-4">
        <Wand2 className="w-4 h-4 text-v2-text-muted" />
        <h2 className="text-sm font-semibold text-v2-text uppercase tracking-[0.12em]">Live provider probe</h2>
      </div>
      <div data-testid="admin-ask-probe-warning" className="mb-4 flex items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/5 text-amber-200 text-xs">
        <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          Probe is an <strong>explicit</strong> admin action. It sends a fixed diagnostic prompt to the provider —
          no learner content, no Academy material, no PII. Rate-limited to 6 attempts per minute.
        </div>
      </div>
      <button
        data-testid="admin-ask-probe-run"
        onClick={run}
        disabled={probing}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-v2-border text-sm text-v2-text hover:bg-v2-surface-muted disabled:opacity-50"
      >
        {probing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
        {probing ? "Probing…" : "Run diagnostic probe"}
      </button>
      {result && (
        <div data-testid="admin-ask-probe-result" className={`mt-4 p-3 rounded-md border text-xs ${
          result.ok ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-300" : "border-red-500/40 bg-red-500/5 text-red-300"
        }`}>
          <div className="flex items-center gap-2 font-medium">
            {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {result.message}
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-v2-text-muted">
            <div><dt className="inline">Provider:</dt> <dd className="inline text-v2-text">{result.provider || "—"}</dd></div>
            <div><dt className="inline">Model:</dt> <dd className="inline text-v2-text">{result.model || "—"}</dd></div>
            <div><dt className="inline">Latency:</dt> <dd className="inline text-v2-text">{result.latency_ms} ms</dd></div>
            {result.error_code && (
              <div><dt className="inline">Error code:</dt> <dd className="inline text-v2-text">{result.error_code}</dd></div>
            )}
          </dl>
        </div>
      )}
      {error && (
        <div data-testid="admin-ask-probe-error" className="mt-4 p-3 rounded-md border border-red-500/40 bg-red-500/5 text-red-300 text-xs">
          {error}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Summary panel — 5 canonical index states
// ---------------------------------------------------------------------------

function SummaryPanel() {
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("loading");

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const r = await api.get("/admin/academy/ask/grounding/summary");
      setSummary(r.data);
      setStatus("ready");
    } catch (err) {
      setStatus("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="admin-ask-summary" className="mt-8 rounded-xl border border-v2-border bg-v2-surface p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-v2-text-muted" />
          <h2 className="text-sm font-semibold text-v2-text uppercase tracking-[0.12em]">Grounding index states</h2>
        </div>
        <button
          data-testid="admin-ask-summary-refresh"
          onClick={load}
          className="text-xs text-v2-text-muted hover:text-v2-text inline-flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      {status === "loading" && (
        <div className="text-xs text-v2-text-muted flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}
      {status === "ready" && summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(STATE_META).map(([key, meta]) => {
            const Icon = meta.Icon;
            const count = summary.states?.[key] ?? 0;
            return (
              <div
                key={key}
                data-testid={`admin-ask-summary-state-${key}`}
                className={`rounded-lg border px-3 py-3 ${meta.cls}`}
              >
                <Icon className="w-4 h-4" />
                <p className="text-lg font-semibold text-v2-text mt-1">{count}</p>
                <p className="text-[10px] uppercase tracking-widest text-v2-text-muted">{meta.label}</p>
              </div>
            );
          })}
          <div className="rounded-lg border border-v2-border bg-v2-bg px-3 py-3 md:col-span-5 flex items-center justify-between">
            <span className="text-xs text-v2-text-muted uppercase tracking-widest">Total active chunks</span>
            <span data-testid="admin-ask-summary-total-chunks" className="text-lg text-v2-text font-semibold">{summary.total_chunks}</span>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sources table
// ---------------------------------------------------------------------------

function SourcesTable() {
  const [sources, setSources] = useState([]);
  const [status, setStatus] = useState("loading");
  const [stateFilter, setStateFilter] = useState("");
  const [reindexing, setReindexing] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const params = {};
      if (stateFilter) params.state = stateFilter;
      const r = await api.get("/admin/academy/ask/grounding/sources", { params });
      setSources(r.data.items || []);
      setStatus("ready");
    } catch (err) {
      setStatus("error");
    }
  }, [stateFilter]);

  useEffect(() => { load(); }, [load]);

  const reindex = async (row) => {
    setReindexing(row.id);
    try {
      await api.post(`/admin/academy/ask/grounding/sources/${row.source_type}/${row.source_id}/reindex`);
      toast.success("Reindex queued.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Reindex failed.");
    } finally {
      setReindexing(null);
    }
  };

  return (
    <section data-testid="admin-ask-sources" className="mt-8 rounded-xl border border-v2-border bg-v2-surface p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-v2-text-muted" />
          <h2 className="text-sm font-semibold text-v2-text uppercase tracking-[0.12em]">Sources</h2>
        </div>
        <select
          data-testid="admin-ask-sources-state-filter"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="bg-v2-bg border border-v2-border rounded-md px-2 py-1 text-xs text-v2-text"
        >
          <option value="">All states</option>
          {Object.entries(STATE_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
      </div>

      {status === "loading" && (
        <div className="text-xs text-v2-text-muted flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}
      {status === "ready" && sources.length === 0 && (
        <div data-testid="admin-ask-sources-empty" className="border border-dashed border-v2-border rounded-lg p-10 text-center text-v2-text-muted text-xs">
          No indexed sources. Publish a program lesson or resource — reindex fires automatically on publish.
        </div>
      )}
      {status === "ready" && sources.length > 0 && (
        <table data-testid="admin-ask-sources-table" className="w-full text-sm border border-v2-border rounded-lg overflow-hidden">
          <thead className="bg-v2-bg">
            <tr className="text-left text-v2-text-muted text-xs uppercase tracking-widest">
              <th className="p-3">Source</th>
              <th className="p-3">State</th>
              <th className="p-3">Chunks</th>
              <th className="p-3">Last indexed</th>
              <th className="p-3">Last error</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((r) => {
              const meta = STATE_META[r.state] || STATE_META.indexing_pending;
              const Icon = meta.Icon;
              return (
                <tr key={r.id} data-testid={`admin-ask-source-row-${r.id}`} className="border-t border-v2-border">
                  <td className="p-3 text-xs">
                    <div className="text-v2-text">{r.source_type}</div>
                    <div className="text-[10px] text-v2-text-muted font-mono">{r.source_id}</div>
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </td>
                  <td className="p-3 text-xs">{r.chunk_count}</td>
                  <td className="p-3 text-[11px] text-v2-text-muted">
                    {r.last_indexed_at ? new Date(r.last_indexed_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-3 text-[11px] text-v2-text-muted">
                    {r.last_error_code || "—"}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      data-testid={`admin-ask-source-reindex-${r.id}`}
                      onClick={() => reindex(r)}
                      disabled={reindexing === r.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-v2-border text-xs text-v2-text hover:bg-v2-surface-muted disabled:opacity-50"
                    >
                      {reindexing === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                      Reindex
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
